import { useReportInstagramCampaigns } from './useReportInstagramCampaigns';
import ReportCampaignsTable from '../CampaignsTable/ReportCampaignsTable';
import InstagramReportPostsTable from 'Modules/Plans/Reports/Report/Posts/InstagramReportPostsTable/InstagramReportPostsTable';
import useUrlState from 'Hooks/useUrlState';
import { ExpandedState } from '@tanstack/react-table';
import { useMemo } from 'react';
import { useReorderReportCampaigns } from '../hooks/useReorderReportCampaigns';
import InstagramAudioModal from 'Modules/Plans/Audios/AudioModal/InstagramAudioModal/InstagramAudioModal';
import { PostUrlClipboardButton } from 'Modules/Plans/CopyToClipboard/PostUrlTable';
import { useCopyReportCampaignPostUrls } from '../../hooks/useCopyReportCampaignPostUrls';
import styles from '../ReportCampaigns.module.css';

type UrlState = Partial<{
    expanded: string;
    audioId: number;
}>;

const ReportInstagramCampaigns = () => {
    const [urlState, setUrlState] = useUrlState<UrlState>();
    const {
        rows,
        isLoading: areCampaignsLoading,
        isTeamsLoading,
        isProjectsLoading,
        isAudioTimeSeriesLoading,
        isAudioTimeSeriesError,
        getIsBrandLoading,
        getIsCampaignStatsLoading,
    } = useReportInstagramCampaigns();

    const expandedState = useMemo(() => {
        const expanded = urlState?.expanded;

        if (!expanded) {
            return {};
        }

        return expanded.split(',').reduce(
            (acc, campaignId) => {
                acc[campaignId] = true;
                return acc;
            },
            {} as Record<string, boolean>
        );
    }, [urlState]);

    const setExpandedState = (expanded: ExpandedState) => {
        const expandedKeys = Object.keys(expanded).filter((key) => expanded[key as keyof ExpandedState]);
        setUrlState({ expanded: expandedKeys.join(',') });
    };

    const reorderCampaigns = useReorderReportCampaigns('instagram');

    const { isLoading: isCopyingPostUrls, copyReportCampaignPostUrls } = useCopyReportCampaignPostUrls(
        'instagram',
        rows
    );

    return (
        <>
            <div className={styles.actions}>
                <PostUrlClipboardButton
                    onClick={copyReportCampaignPostUrls}
                    disabled={!rows.length}
                    isLoading={isCopyingPostUrls}
                />
            </div>
            <ReportCampaignsTable
                data={rows}
                isLoading={areCampaignsLoading}
                expandedState={expandedState}
                onExpandedChange={setExpandedState}
                renderSubComponent={(row) => <InstagramReportPostsTable campaign={row.original} />}
                meta={{
                    isLoading: areCampaignsLoading,
                    getIsCampaignStatsLoading: (row) => getIsCampaignStatsLoading(row.id),
                    getIsBrandLoading: (row) => getIsBrandLoading(row.id),
                    getIsProjectLoading: () => isProjectsLoading,
                    getIsTeamLoading: () => isTeamsLoading,
                    getIsTimeSeriesLoading: () => isAudioTimeSeriesLoading,
                    getTimeSeriesHasError: () => isAudioTimeSeriesError,
                    onAudioGrowthCellClick: (row) => {
                        if (!row.instagram_details?.audio_id) {
                            return;
                        }

                        setUrlState({ audioId: row.instagram_details.audio_id });
                    },
                }}
                onDragEnd={reorderCampaigns}
            />

            {urlState.audioId && (
                <InstagramAudioModal
                    isOpen={Boolean(urlState.audioId)}
                    onClose={() => setUrlState({ audioId: undefined })}
                    audioId={Number(urlState.audioId)}
                />
            )}
        </>
    );
};

export default ReportInstagramCampaigns;
